import styles from '../../MagicUpload.module.scss';
import AnimatedFolder from '../AnimatedFolder/AnimatedFolder';
import React, { useEffect, useRef, useId } from 'react';
import useMnemosyneUpload from './useMnemosyneUpload';
import { useAppSelector } from '../../../../app/hooks';
import { useSearchParams } from 'react-router-dom';
import { MagicUploadFileData } from '../../useFetchDataForMU';

interface MagicUploadCenterProps {
    tabState: string;
    fileData: MagicUploadFileData[];
    setFileData: React.Dispatch<React.SetStateAction<MagicUploadFileData[]>>;
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    orientation: 'vertical' | 'horizontal';
    folderDimension?: { width: string; height: string };
    componentStyle?: React.CSSProperties;
    /**
     * Optional override: when provided, replaces the internal useMnemosyneUpload
     * file-handling with the caller's own handler. The files passed here are the
     * raw FileList from drag-and-drop or the file input. Used by Magic Sign to
     * wire its own upload chain without forking this component.
     */
    onFilesSelected?: (files: FileList) => void;
    /**
     * Optional MIME type filter for the file input accept attribute.
     * Defaults to '*' (all types) when not provided.
     * Example: 'application/pdf'
     */
    acceptTypes?: string;
    /** Optional override for the drop-zone caption. Defaults to "Drag and drop your files here". */
    dropText?: string;
}

const MagicUploadCenter = (props: MagicUploadCenterProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileInputId = useId();
    const [params] = useSearchParams();
    const config = useAppSelector((state) => state.investor.config);
    const companyConfig = useAppSelector((state) => state.company.config);
    const [categoryId, setCategoryId] = React.useState('');
    useEffect(() => {
        const isCompany = params.get('isCompany') === 'true';
        let selectedConfig = isCompany ? companyConfig : config;
        if (isCompany && !companyConfig) selectedConfig = config;

        if (!selectedConfig?.dataConfig?.staging?.categoryId) {
            console.warn('Missing required configuration for categoryId');
        }
        setCategoryId(selectedConfig?.dataConfig?.staging?.categoryId);
    }, [config, companyConfig]);

    const { tabState, fileData, setFileData, setLoading, folderDimension, onFilesSelected, acceptTypes, dropText } = props;

    const mnemosyneHandlers = useMnemosyneUpload(tabState, fileData, setFileData, categoryId, setLoading);

    // When an external handler is provided, wire drag/drop/click through it;
    // otherwise fall through to the internal useMnemosyneUpload handlers.
    const handleFileChange = (files: FileList | null) => {
        if (!files) return;
        if (onFilesSelected) {
            onFilesSelected(files);
        } else {
            mnemosyneHandlers.handleFileChange(files);
        }
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (onFilesSelected) {
            if (event.dataTransfer.files) onFilesSelected(event.dataTransfer.files);
        } else {
            await mnemosyneHandlers.handleDrop(event);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div
            style={props.componentStyle}
            data-testid="file-input"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
            className={
                props.orientation === 'vertical'
                    ? `${styles.drawerBody} ${styles.folderContainer}`
                    : `${styles.drawerBody} ${styles.folderContainerHorizontal}`
            }
        >
            <AnimatedFolder folderDimensions={folderDimension} />
            <div className={styles.Text}>{dropText ?? 'Drag and drop your files here'}</div>
            <input
                ref={fileInputRef}
                type="file"
                id={fileInputId}
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(e.target.files)}
                multiple
                accept={acceptTypes}
            />
        </div>
    );
};

export default MagicUploadCenter;
